<template>
	<div class="text-center">
		<v-dialog v-model="activeDialog" transition="dialog-bottom-transition" fullscreen>
			<v-card>
				<v-toolbar>
					<v-toolbar-title>Trace Worksheet Analyzer</v-toolbar-title>
					<v-toolbar-items>
						<v-btn prepend-icon="mdi-close" @click="closeAnalyzer">Close</v-btn>
					</v-toolbar-items>
				</v-toolbar>

				<WaitDialog :is-active="isLoading" title="Loading trace worksheet..." />
				<v-container fluid>
					<!-- Error display -->
					<v-container v-if="errorMessage" class="px-0">
						<UserNotification :message="errorMessage" :closable="true" />
					</v-container>

					<!-- Header card -->
					<v-card v-if="parsedWorksheet" class="mb-4" variant="outlined">
						<v-card-text>
							<v-row align="center">
								<v-col cols="3"><strong>Policy:</strong> {{ parsedWorksheet.header.policyNumber }}</v-col>
								<v-col cols="3"><strong>Correlation ID:</strong> {{ parsedWorksheet.header.correlationId }}</v-col>
								<v-col cols="2"><strong>Effective:</strong> {{ parsedWorksheet.header.effectiveDate }}</v-col>
								<v-col cols="2"><strong>Entry:</strong> {{ parsedWorksheet.header.entryDate }}</v-col>
								<v-col cols="1"><strong>Environment:</strong> {{ parsedWorksheet.header.environment }}</v-col>
								<v-col cols="1" class="text-right">
									<v-tooltip text="Download Trace Worksheet" location="bottom">
										<template #activator="{ props: tooltipProps }">
											<v-btn v-bind="tooltipProps" icon="mdi-download" color="primary" variant="text"
												@click="downloadFile" />
										</template>
									</v-tooltip>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>

					<!-- Filters toolbar -->
					<v-card v-if="parsedWorksheet" class="mb-4" variant="outlined">
						<v-card-text>
							<v-row>
								<v-col cols="2">
									<v-autocomplete v-model="filters.algorithmName" :items="distinctAlgorithmNames"
										label="Algorithm Name" clearable variant="solo-filled" density="compact" />
								</v-col>
								<v-col cols="2">
									<v-autocomplete v-model="filters.state" :items="distinctStates" label="State" clearable
										variant="solo-filled" density="compact" />
								</v-col>
								<v-col cols="2">
									<v-autocomplete v-model="filters.lineOfBusiness" :items="distinctLinesOfBusiness"
										label="Line of Business" clearable variant="solo-filled" density="compact" />
								</v-col>
								<v-col cols="2">
									<v-autocomplete v-model="filters.rateKeyName" :items="distinctRateKeyNames" label="Rate Key"
										clearable variant="solo-filled" density="compact" />
								</v-col>
								<v-col cols="3">
									<v-text-field v-model="filters.searchText" label="Search (rate keys, activity, etc.)" clearable
										prepend-inner-icon="mdi-magnify" variant="solo-filled" density="compact" />
								</v-col>
								<v-col cols="1" class="d-flex align-center">
									<v-btn @click="clearFilters" color="primary" variant="text" size="small">
										Clear All
									</v-btn>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>

					<!-- Selection indicator -->
					<v-slide-y-transition>
						<v-card v-if="parsedWorksheet && selectedAlgorithms.size > 0" class="mb-4 selection-bar" color="primary" variant="tonal">
							<v-card-text class="d-flex align-center py-2">
								<v-icon class="mr-2">mdi-checkbox-multiple-marked-outline</v-icon>
								<strong>{{ selectedAlgorithms.size }} algorithm{{ selectedAlgorithms.size > 1 ? 's' : '' }} selected</strong>
								<span v-if="showSelectedOnly" class="ml-2 text-caption">(comparing selected algorithms)</span>
								<span v-else class="ml-2 text-caption">(click "Show Selected" to compare)</span>
								<v-spacer />
								<v-btn @click="clearSelection" variant="outlined" size="small" color="primary">
									Clear Selection
								</v-btn>
							</v-card-text>
						</v-card>
					</v-slide-y-transition>

					<!-- Main content: Stats + Algorithms -->
					<v-row v-if="parsedWorksheet && stats" no-gutters>
						<!-- Stats sidebar: drawer style -->
						<v-col :cols="statsVisible ? 3 : 'auto'" class="pr-3 stats-sidebar">
							<!-- Collapsed strip -->
							<v-card v-if="!statsVisible" variant="outlined" class="stats-drawer-collapsed" @click="statsVisible = true">
								<v-icon size="small" class="mb-2">mdi-chevron-right</v-icon>
								<span class="stats-drawer-label">Statistics</span>
							</v-card>

							<!-- Expanded panel -->
							<v-card v-else variant="outlined" class="text-left">
								<v-card-title class="text-subtitle-1 d-flex align-center">
									<span>Statistics</span>
									<v-spacer />
									<v-btn @click="statsVisible = false" icon variant="text" size="x-small"
										title="Collapse statistics panel">
										<v-icon>mdi-chevron-left</v-icon>
									</v-btn>
								</v-card-title>
								<v-card-text>
									<v-list density="compact">
										<v-list-item>
											<strong>Total Algorithms:</strong> {{ stats.totalAlgorithms }}
										</v-list-item>
										<v-list-item>
											<strong>Filtered:</strong> {{ filteredAlgorithms.length }}
										</v-list-item>
										<v-list-item>
											<strong>Distinct Names:</strong> {{ stats.distinctAlgorithmNames.length }}
										</v-list-item>
										<v-list-item>
											<strong>States:</strong> {{ stats.distinctStates.join(', ') }}
										</v-list-item>
										<v-list-item>
											<strong>Lines of Business:</strong> {{ stats.distinctLinesOfBusiness.join(', ') }}
										</v-list-item>
										<v-list-item>
											<strong>Insurance Lines:</strong> {{ stats.distinctInsuranceLines.join(', ') }}
										</v-list-item>

										<v-divider class="my-2" />
										<v-list-subheader>Activity Breakdown</v-list-subheader>
										<v-list-item>
											<v-icon size="x-small" color="info" class="mr-1">mdi-help-circle-outline</v-icon>
											Conditions: {{ stats.totalConditions }}
										</v-list-item>
										<v-list-item>
											<v-icon size="x-small" color="success" class="mr-1">mdi-pencil-outline</v-icon>
											Set Operations: {{ stats.totalSetOperations }}
										</v-list-item>
										<v-list-item>
											<v-icon size="x-small" color="warning" class="mr-1">mdi-key-outline</v-icon>
											Rate Key Lookups: {{ stats.totalRateKeyLookups }}
										</v-list-item>
										<v-list-item>
											<v-icon size="x-small" color="secondary" class="mr-1">mdi-file-document-outline</v-icon>
											Form Operations: {{ stats.totalFormOperations }}
										</v-list-item>
										<v-list-item>
											<v-icon size="x-small" color="primary" class="mr-1">mdi-calculator-variant-outline</v-icon>
											Arithmetic: {{ stats.totalArithmeticOperations }}
										</v-list-item>
										<v-list-item>
											<v-icon size="x-small" color="info" class="mr-1">mdi-check-decagram-outline</v-icon>
											Evaluations: {{ stats.totalEvaluations }}
										</v-list-item>

										<v-divider class="my-2" />
										<v-list-subheader>Sections</v-list-subheader>
										<v-list-item v-for="s in stats.sectionSummaries" :key="s.name">
											{{ s.name }}: {{ s.algorithmCount }}
										</v-list-item>
									</v-list>
								</v-card-text>
							</v-card>
						</v-col>

						<!-- Algorithm list -->
						<v-col class="algorithms-column">
							<v-card variant="outlined" class="text-left">
								<v-card-title class="text-subtitle-1 d-flex align-center">
									<span>Algorithms ({{ filteredAlgorithms.length }} of {{ stats.totalAlgorithms }})</span>
									<v-spacer />
									<v-btn v-if="showSelectedOnly"
										@click="expandAllSelected"
										variant="text" size="small" class="mr-1"
										prepend-icon="mdi-arrow-expand-vertical">
										Expand All
									</v-btn>
									<v-btn v-if="showSelectedOnly"
										@click="collapseAllSelected"
										variant="text" size="small" class="mr-2"
										prepend-icon="mdi-arrow-collapse-vertical">
										Collapse All
									</v-btn>
									<v-btn v-if="selectedAlgorithms.size > 0 && !showSelectedOnly"
										@click="enterSelectedView"
										variant="tonal" color="primary" size="small" class="mr-2"
										prepend-icon="mdi-filter-check-outline">
										Show Selected ({{ selectedAlgorithms.size }})
									</v-btn>
									<v-btn v-if="showSelectedOnly"
										@click="showSelectedOnly = false"
										variant="tonal" color="secondary" size="small" class="mr-2"
										prepend-icon="mdi-filter-remove-outline">
										Show All
									</v-btn>
								</v-card-title>
								<v-card-text class="algorithms-scroll">
									<v-expansion-panels v-model="openPanels" multiple>
										<v-expansion-panel v-for="algo in displayedAlgorithms" :key="algo.lineNumber"
											:value="algo.lineNumber"
											:class="{ 'selected-panel': isSelected(algo) }">
											<v-expansion-panel-title>
												<v-row no-gutters align="center">
													<v-col cols="auto" class="mr-2">
														<v-checkbox-btn
															:model-value="isSelected(algo)"
															@update:model-value="toggleSelection(algo)"
															@click.stop
															color="primary"
															density="compact"
														/>
													</v-col>
													<v-col cols="1">
														<v-chip size="x-small" color="primary" variant="tonal">
															{{ algo.index }}/{{ algo.total }}
														</v-chip>
													</v-col>
													<v-col cols="3">
														<strong>{{ algo.algorithmName }}</strong>
													</v-col>
													<v-col cols="2">
														<v-chip v-if="algo.state" size="x-small" color="info" variant="tonal">
															State: {{ algo.state }}
														</v-chip>
													</v-col>
													<v-col cols="2">
														<v-chip v-if="algo.line" size="x-small" color="warning" variant="tonal">
															Line: {{ algo.line }}
														</v-chip>
													</v-col>
													<v-col cols="1">
														<v-chip v-if="algo.lob" size="x-small" color="success" variant="tonal">
															{{ algo.lob }}
														</v-chip>
													</v-col>
													<v-col cols="2" class="text-caption text-medium-emphasis">
														{{ algo.shortName }} (L{{ algo.lineNumber }})
													</v-col>
												</v-row>
											</v-expansion-panel-title>
											<v-expansion-panel-text>
												<div v-if="algo.product || algo.unit || algo.coverage" class="mb-3 text-caption">
													<v-chip v-if="algo.product" size="x-small" variant="tonal" class="mr-1">Product: {{ algo.product }}</v-chip>
													<v-chip v-if="algo.unit" size="x-small" variant="tonal" class="mr-1">Unit: {{ algo.unit }}</v-chip>
													<v-chip v-if="algo.coverage" size="x-small" variant="tonal">Coverage: {{ algo.coverage }}</v-chip>
												</div>

												<!-- Conditions table -->
												<div v-if="getActivitiesByType(algo, 'condition').length" class="mb-4">
													<div class="section-header">
														<v-icon size="x-small" color="info" class="mr-1">mdi-help-circle-outline</v-icon>
														Conditions ({{ getActivitiesByType(algo, 'condition').length }})
													</div>
													<v-data-table
														:headers="conditionHeaders"
														:items="getActivitiesByType(algo, 'condition')"
														:items-per-page="-1"
														hide-default-footer
														density="compact"
														class="activity-table"
													>
														<template v-slot:[`item.attribute`]="{ item }">
															<template v-if="item.condition">
																<span v-if="filters.searchText" v-html="highlightSearch(item.condition.attribute, filters.searchText)" />
																<span v-else>{{ item.condition.attribute }}</span>
															</template>
															<span v-else class="text-caption">{{ item.rawText }}</span>
														</template>
														<template v-slot:[`item.actualValue`]="{ item }">
															<span class="font-weight-medium text-no-wrap">{{ item.condition?.actualValue }}</span>
														</template>
														<template v-slot:[`item.operator`]="{ item }">
															<span class="text-no-wrap">{{ item.condition?.operator }}</span>
														</template>
														<template v-slot:[`item.expectedValue`]="{ item }">
															<span class="text-no-wrap">{{ item.condition?.expectedValue }}</span>
														</template>
														<template v-slot:[`item.result`]="{ item }">
															<v-chip v-if="item.condition" :color="item.condition.result ? 'success' : 'error'" size="x-small" variant="tonal">
																{{ item.condition.result ? 'TRUE' : 'FALSE' }}
															</v-chip>
														</template>
													</v-data-table>
												</div>

												<!-- Set operations table -->
												<div v-if="getActivitiesByType(algo, 'set').length" class="mb-4">
													<div class="section-header">
														<v-icon size="x-small" color="success" class="mr-1">mdi-pencil-outline</v-icon>
														Set Operations ({{ getActivitiesByType(algo, 'set').length }})
													</div>
													<v-data-table
														:headers="setHeaders"
														:items="getActivitiesByType(algo, 'set')"
														:items-per-page="-1"
														hide-default-footer
														density="compact"
														class="activity-table"
													>
														<template v-slot:[`item.targetType`]="{ item }">
															<v-chip v-if="item.set" size="x-small" variant="tonal" :color="item.set.targetType === 'Attribute' ? 'primary' : 'secondary'">
																{{ item.set.targetType === 'Attribute' ? 'Attr' : 'NRE' }}
															</v-chip>
															<span v-else class="text-caption">{{ item.rawText }}</span>
														</template>
														<template v-slot:[`item.target`]="{ item }">
															<template v-if="item.set">
																<span v-if="filters.searchText" v-html="highlightSearch(item.set.target, filters.searchText)" />
																<span v-else>{{ item.set.target }}</span>
															</template>
														</template>
														<template v-slot:[`item.value`]="{ item }">
															<span class="font-weight-medium">{{ item.set?.value }}</span>
														</template>
													</v-data-table>
												</div>

												<!-- Rate Keys (stacked: lookup header + results per key) -->
												<div v-if="getRateKeyGroups(algo).length" class="mb-4">
													<div class="section-header">
														<v-icon size="x-small" color="warning" class="mr-1">mdi-key-outline</v-icon>
														Rate Keys
													</div>
													<div v-for="(group, gIdx) in getRateKeyGroups(algo)" :key="'rkg'+gIdx" class="rate-key-block mb-2">
														<div class="rate-key-name">
															<span v-if="filters.searchText" v-html="highlightSearch(group.keyName, filters.searchText)" />
															<span v-else>{{ group.keyName }}</span>
														</div>
														<div v-if="group.lookup" class="rate-key-details">
															<v-chip size="x-small" variant="tonal" class="mr-1">Eff: {{ group.lookup.effectiveDate }}</v-chip>
															<v-chip size="x-small" variant="tonal" class="mr-1">State: {{ group.lookup.state }}</v-chip>
															<v-chip size="x-small" variant="tonal" class="mr-1">LOB: {{ group.lookup.lob }}</v-chip>
															<v-chip size="x-small" variant="tonal" class="mr-1">Line: {{ group.lookup.line }}</v-chip>
															<v-chip size="x-small" variant="tonal" class="mr-1">Product: {{ group.lookup.product }}</v-chip>
															<v-chip size="x-small" variant="tonal">Coverage: {{ group.lookup.coverage }}</v-chip>
														</div>
														<v-data-table
															v-if="group.results.length"
															:headers="rateKeyResultHeaders"
															:items="group.results"
															:items-per-page="-1"
															hide-default-footer
															density="compact"
															class="activity-table rate-key-results"
														>
															<template v-slot:[`item.keyName`]="{ item }">
																<span v-if="filters.searchText" v-html="highlightSearch(item.keyName, filters.searchText)" />
																<span v-else>{{ item.keyName }}</span>
															</template>
															<template v-slot:[`item.lookupValue`]="{ item }">
																<span class="font-weight-medium">{{ item.lookupValue }}</span>
															</template>
															<template v-slot:[`item.result`]="{ item }">
																<span class="font-weight-medium">{{ item.result }}</span>
															</template>
														</v-data-table>
													</div>
												</div>

												<!-- Form operations -->
												<div v-if="getActivitiesByType(algo, 'form').length" class="mb-4">
													<div class="section-header">
														<v-icon size="x-small" color="secondary" class="mr-1">mdi-file-document-outline</v-icon>
														Forms ({{ getActivitiesByType(algo, 'form').length }})
													</div>
													<v-data-table
														:headers="formHeaders"
														:items="getActivitiesByType(algo, 'form')"
														:items-per-page="-1"
														hide-default-footer
														density="compact"
														class="activity-table"
													>
														<template v-slot:[`item.formNumber`]="{ item }">
															<span v-if="item.form" class="font-weight-medium">{{ item.form.formNumber }}</span>
															<span v-else class="text-caption">{{ item.rawText }}</span>
														</template>
														<template v-slot:[`item.formEdition`]="{ item }">
															<span v-if="item.form">{{ item.form.formEdition }}</span>
														</template>
														<template v-slot:[`item.action`]="{ item }">
															<v-chip
																v-if="item.form"
																size="x-small"
																variant="tonal"
																:color="item.form.action === 'Attached' ? 'success' : item.form.action === 'Detached' ? 'error' : 'info'"
															>
																{{ item.form.action }}
															</v-chip>
														</template>
														<template v-slot:[`item.location`]="{ item }">
															<span v-if="item.form" class="text-caption">{{ item.form.location }}</span>
														</template>
													</v-data-table>
												</div>

												<!-- Arithmetic -->
												<div v-if="getActivitiesByType(algo, 'arithmetic').length" class="mb-4">
													<div class="section-header">
														<v-icon size="x-small" color="primary" class="mr-1">mdi-calculator-variant-outline</v-icon>
														Arithmetic ({{ getActivitiesByType(algo, 'arithmetic').length }})
													</div>
													<v-data-table
														:headers="arithmeticHeaders"
														:items="getActivitiesByType(algo, 'arithmetic')"
														:items-per-page="-1"
														hide-default-footer
														density="compact"
														class="activity-table"
													>
														<template v-slot:[`item.attribute`]="{ item }">
															<span v-if="item.arithmetic">{{ item.arithmetic.attribute }}</span>
															<span v-else class="text-caption">{{ item.rawText }}</span>
														</template>
														<template v-slot:[`item.currentValue`]="{ item }">
															<span>{{ item.arithmetic?.currentValue }}</span>
														</template>
														<template v-slot:[`item.operator`]="{ item }">
															<span>{{ item.arithmetic?.operator }}</span>
														</template>
														<template v-slot:[`item.operand`]="{ item }">
															<template v-if="item.arithmetic">
																<span v-if="item.arithmetic.operandLabel" class="text-caption text-medium-emphasis">{{ item.arithmetic.operandLabel }} </span>
																{{ item.arithmetic.operand }}
															</template>
														</template>
														<template v-slot:[`item.result`]="{ item }">
															<span class="font-weight-medium">{{ item.arithmetic?.result }}</span>
														</template>
													</v-data-table>
												</div>

												<!-- Evaluations -->
												<div v-if="getActivitiesByType(algo, 'evaluation').length" class="mb-4">
													<div class="section-header">
														<v-icon size="x-small" color="info" class="mr-1">mdi-check-decagram-outline</v-icon>
														Evaluations ({{ getActivitiesByType(algo, 'evaluation').length }})
													</div>
													<v-data-table
														:headers="evaluationHeaders"
														:items="getActivitiesByType(algo, 'evaluation')"
														:items-per-page="-1"
														hide-default-footer
														density="compact"
														class="activity-table"
													>
														<template v-slot:[`item.attribute`]="{ item }">
															<span v-if="item.evaluation">{{ item.evaluation.attribute }}</span>
															<span v-else class="text-caption">{{ item.rawText }}</span>
														</template>
														<template v-slot:[`item.value`]="{ item }">
															<span class="font-weight-medium">{{ item.evaluation?.value }}</span>
														</template>
														<template v-slot:[`item.condition`]="{ item }">
															<span>{{ item.evaluation?.condition }}</span>
														</template>
														<template v-slot:[`item.qualifies`]="{ item }">
															<span
																v-if="item.evaluation"
																class="font-weight-bold"
																:class="item.evaluation.qualifies ? 'text-success' : 'text-error'"
															>
																{{ item.evaluation.qualifies ? 'Yes' : 'No' }}
															</span>
														</template>
														<template v-slot:[`item.target`]="{ item }">
															<span>{{ item.evaluation?.target }}</span>
														</template>
													</v-data-table>
												</div>

												<!-- Other/unclassified -->
												<div v-if="getActivitiesByType(algo, 'other').length" class="mb-3">
													<div class="section-header">
														<v-icon size="x-small" color="grey" class="mr-1">mdi-dots-horizontal</v-icon>
														Other ({{ getActivitiesByType(algo, 'other').length }})
													</div>
													<div v-for="(act, idx) in getActivitiesByType(algo, 'other')" :key="'o'+idx"
														class="other-line text-caption py-1">
														<span v-if="filters.searchText"
															v-html="highlightSearch(act.rawText, filters.searchText)" />
														<span v-else>{{ act.rawText }}</span>
													</div>
												</div>

												<div v-if="algo.activities.length === 0" class="text-caption text-medium-emphasis">
													No activity lines recorded for this execution.
												</div>
											</v-expansion-panel-text>
										</v-expansion-panel>
									</v-expansion-panels>

									<div v-if="filteredAlgorithms.length === 0" class="text-center py-6 text-medium-emphasis">
										No algorithms match the current filters.
									</div>
								</v-card-text>

								<!-- Pagination -->
								<v-card-actions v-if="totalPages > 1 && !showSelectedOnly">
									<v-row align="center">
										<v-col cols="3">
											<v-select v-model="itemsPerPage" :items="[10, 25, 50, 100]" label="Per page"
												density="compact" variant="solo-filled" />
										</v-col>
										<v-col cols="6" class="d-flex justify-center">
											<v-pagination v-model="currentPage" :length="totalPages" :total-visible="7" />
										</v-col>
										<v-col cols="3" class="d-flex align-center justify-end text-caption">
											{{ filteredAlgorithms.length }} algorithms
										</v-col>
									</v-row>
								</v-card-actions>
							</v-card>
						</v-col>
					</v-row>
				</v-container>
			</v-card>
		</v-dialog>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import WaitDialog from '@/components/WaitDialog.vue';
import UserNotification from '@/components/UserNotification.vue';
import { useTraceWorksheetAnalyzer } from '@/composables/TraceWorksheetAnalyzer/UseTraceWorksheetAnalyzer';
import { ActivityType, ActivityLine, AlgorithmExecution, RateKeyLookupDetail, RateKeyResultDetail } from '@/models/TraceWorksheetModels';

const props = defineProps<{
	isActive: boolean;
	docUrl: string;
}>();

const emit = defineEmits<{ (e: 'closed'): void }>();

const activeDialog = ref(props.isActive);

// Reactive docUrl ref that updates when the prop changes
const docUrlRef = ref<string>(props.docUrl);

// Single watcher to ensure docUrlRef is updated before loadWorksheet fires
watch(
	[() => props.isActive, () => props.docUrl],
	([newActive, newUrl], [oldActive]) => {
		docUrlRef.value = newUrl;
		activeDialog.value = newActive;
		// Reset state and reload when dialog opens
		if (newActive && !oldActive) {
			resetState();
			loadWorksheet();
		}
	}
);

const {
	isLoading,
	errorMessage,
	parsedWorksheet,
	filters,
	stats,
	filteredAlgorithms,
	paginatedAlgorithms,
	distinctAlgorithmNames,
	distinctStates,
	distinctLinesOfBusiness,
	distinctRateKeyNames,
	itemsPerPage,
	currentPage,
	totalPages,
	clearFilters,
	loadWorksheet,
} = useTraceWorksheetAnalyzer(docUrlRef);

// Stats panel visibility
const statsVisible = ref<boolean>(true);

// Selection state
const selectedAlgorithms = ref<Set<number>>(new Set());
const showSelectedOnly = ref<boolean>(false);
const openPanels = ref<number[]>([]);

function resetState() {
	selectedAlgorithms.value = new Set();
	showSelectedOnly.value = false;
	openPanels.value = [];
	statsVisible.value = true;
	clearFilters();
}

function closeAnalyzer() {
	activeDialog.value = false;
	emit('closed');
}

const isSelected = (algo: AlgorithmExecution): boolean => {
	return selectedAlgorithms.value.has(algo.lineNumber);
};

const toggleSelection = (algo: AlgorithmExecution) => {
	const newSet = new Set(selectedAlgorithms.value);
	if (newSet.has(algo.lineNumber)) {
		newSet.delete(algo.lineNumber);
		if (newSet.size === 0) {
			showSelectedOnly.value = false;
		}
	} else {
		newSet.add(algo.lineNumber);
	}
	selectedAlgorithms.value = newSet;
};

const clearSelection = () => {
	selectedAlgorithms.value = new Set();
	showSelectedOnly.value = false;
	openPanels.value = [];
};

const enterSelectedView = () => {
	showSelectedOnly.value = true;
	openPanels.value = [...selectedAlgorithms.value];
};

const expandAllSelected = () => {
	openPanels.value = [...selectedAlgorithms.value];
};

const collapseAllSelected = () => {
	openPanels.value = [];
};

const displayedAlgorithms = computed(() => {
	if (showSelectedOnly.value && selectedAlgorithms.value.size > 0) {
		return filteredAlgorithms.value.filter(a => selectedAlgorithms.value.has(a.lineNumber));
	}
	return paginatedAlgorithms.value;
});

const conditionHeaders = [
	{ title: 'Attribute', key: 'attribute', sortable: false },
	{ title: 'Value', key: 'actualValue', sortable: false },
	{ title: 'Op', key: 'operator', sortable: false },
	{ title: 'Expected', key: 'expectedValue', sortable: false },
	{ title: 'Result', key: 'result', sortable: false },
];

const setHeaders = [
	{ title: 'Type', key: 'targetType', sortable: false },
	{ title: 'Target', key: 'target', sortable: false },
	{ title: 'Value', key: 'value', sortable: false },
];

const rateKeyResultHeaders = [
	{ title: 'Key Name', key: 'keyName', sortable: false },
	{ title: 'Lookup Value', key: 'lookupValue', sortable: false },
	{ title: 'Result', key: 'result', sortable: false },
];

const formHeaders = [
	{ title: 'Form #', key: 'formNumber', sortable: false },
	{ title: 'Edition', key: 'formEdition', sortable: false },
	{ title: 'Action', key: 'action', sortable: false },
	{ title: 'Location', key: 'location', sortable: false },
];

const arithmeticHeaders = [
	{ title: 'Attribute', key: 'attribute', sortable: false },
	{ title: 'Current', key: 'currentValue', sortable: false },
	{ title: 'Op', key: 'operator', sortable: false },
	{ title: 'Operand', key: 'operand', sortable: false },
	{ title: 'Result', key: 'result', sortable: false },
];

const evaluationHeaders = [
	{ title: 'Attribute', key: 'attribute', sortable: false },
	{ title: 'Value', key: 'value', sortable: false },
	{ title: 'Condition', key: 'condition', sortable: false },
	{ title: 'Qualifies', key: 'qualifies', sortable: false },
	{ title: 'Target', key: 'target', sortable: false },
];

const getActivitiesByType = (algo: AlgorithmExecution, type: ActivityType): ActivityLine[] => {
	return algo.activities.filter(a => a.type === type);
};

const getRateKeyGroups = (algo: AlgorithmExecution): { keyName: string; lookup: RateKeyLookupDetail | null; results: RateKeyResultDetail[] }[] => {
	const groupMap = new Map<string, { lookup: RateKeyLookupDetail | null; results: RateKeyResultDetail[] }>();
	const order: string[] = [];

	for (const act of algo.activities) {
		if (act.type === 'rateKeyLookup' && act.rateKeyLookup) {
			const name = act.rateKeyLookup.keyName;
			if (!groupMap.has(name)) {
				groupMap.set(name, { lookup: null, results: [] });
				order.push(name);
			}
			const group = groupMap.get(name);
			if (group) {
				group.lookup = act.rateKeyLookup;
			}
		}
		if (act.type === 'rateKeyResult' && act.rateKeyResult) {
			const name = act.rateKeyResult.keyName;
			if (!groupMap.has(name)) {
				groupMap.set(name, { lookup: null, results: [] });
				order.push(name);
			}
			const group = groupMap.get(name);
			if (group) {
				group.results.push(act.rateKeyResult);
			}
		}
	}

	return order
		.map(keyName => {
			const group = groupMap.get(keyName);
			if (!group) {
				return null;
			}

			return { keyName, ...group };
		})
		.filter((group): group is { keyName: string; lookup: RateKeyLookupDetail | null; results: RateKeyResultDetail[] } => group !== null);
};

const downloadFile = () => {
	if (docUrlRef.value) {
		window.location.href = docUrlRef.value;
	}
};

const highlightSearch = (text: string, searchTerm: string): string => {
	if (!searchTerm) return text;
	const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const regex = new RegExp(`(${escaped})`, 'gi');
	return text.replace(regex, '<mark>$1</mark>');
};
</script>

<style scoped>
.section-header {
	font-size: 0.75rem;
	font-weight: 600;
	margin-bottom: 0.25rem;
	padding: 0.25rem 0.5rem;
	border-radius: 0.25rem;
	opacity: 0.85;
}

.activity-table {
	font-size: 0.75rem;
}

.activity-table :deep(th) {
	font-size: 0.7rem !important;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.activity-table td {
	font-family: 'Consolas', 'Courier New', monospace;
	font-size: 0.75rem;
}

.rate-key-block {
	padding: 0.375rem 0.5rem;
	border-inline-start: 0.1875rem solid rgb(var(--v-theme-warning));
}

.rate-key-name {
	font-weight: 600;
	font-size: 0.8rem;
	margin-bottom: 0.25rem;
}

.rate-key-details {
	display: flex;
	flex-wrap: wrap;
	gap: 0.125rem;
}

.rate-key-results {
	margin-top: 0.375rem;
}

.other-line {
	font-family: 'Consolas', 'Courier New', monospace;
	padding-inline-start: 0.5rem;
	border-inline-start: 0.1875rem solid rgb(var(--v-theme-outline));
}

.selected-panel {
	border-inline-start: 0.1875rem solid rgb(var(--v-theme-primary));
	background-color: rgba(var(--v-theme-primary), 0.03);
}

.selection-bar {
	border-inline-start: 0.25rem solid rgb(var(--v-theme-primary));
}

.stats-drawer-collapsed {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0.75rem 0.25rem;
	cursor: pointer;
	height: 100%;
	min-height: 12.5rem;
	border-inline-start: 0.1875rem solid rgb(var(--v-theme-primary));
}

.stats-drawer-collapsed:hover {
	background-color: rgba(var(--v-theme-primary), 0.05);
}

.stats-drawer-label {
	writing-mode: vertical-rl;
	text-orientation: mixed;
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.05em;
	color: rgb(var(--v-theme-primary));
}

.stats-sidebar {
	transition: inline-size 0.3s ease;
}

.algorithms-column {
	min-width: 0;
}

.algorithms-scroll {
	max-height: 60vh;
	overflow-y: auto;
}
</style>
